import { Component } from '@theme/component';

/**
 * Displays volume pricing table with expandable rows.
 * Shows pricing tiers based on quantity thresholds.
 *
 * @extends {Component}
 */
class VolumePricingComponent extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleSelectionChange = this.handleSelectionChange.bind(this);
    document.addEventListener('variant:update', this.handleVariantUpdate);
    this.getSectionRoot()?.addEventListener('change', this.handleSelectionChange);
    this.updateSelectionVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('variant:update', this.handleVariantUpdate);
    this.getSectionRoot()?.removeEventListener('change', this.handleSelectionChange);
  }

  getSectionRoot() {
    return this.closest('.shopify-section') || document;
  }

  getVariantPicker() {
    const section = this.getSectionRoot();
    return section.querySelector(`variant-picker[data-section-id="${this.dataset.sectionId}"]`) || section.querySelector('variant-picker');
  }

  hasCompleteRequiredDropdownSelection() {
    const picker = this.getVariantPicker();
    if (!picker) return this.dataset.hasOnlyDefaultVariant === 'true';

    const selects = Array.from(picker.querySelectorAll('select.variant-option__select'));
    if (selects.length === 0) return this.dataset.hasOnlyDefaultVariant === 'true';

    return selects.every((select) => select instanceof HTMLSelectElement && select.value !== '');
  }

  updateSelectionVisibility() {
    const hasCompleteSelection = this.hasCompleteRequiredDropdownSelection();
    this.hidden = !hasCompleteSelection;
    return hasCompleteSelection;
  }

  handleSelectionChange(event) {
    const picker = this.getVariantPicker();
    if (!picker || !event.target || !picker.contains(event.target)) return;
    this.hidden = true;
  }

  handleVariantUpdate(event) {
    const detail = event?.detail || {};
    if (detail?.data?.productId && this.dataset.productId && detail.data.productId !== this.dataset.productId) return;
    if (!this.hasCompleteRequiredDropdownSelection()) {
      this.hidden = true;
      return;
    }

    const html = detail?.data?.html;
    const replacement = html?.querySelector?.(`volume-pricing[data-section-id="${this.dataset.sectionId}"]`)
      || html?.querySelector?.('volume-pricing');

    if (replacement) {
      const clone = replacement.cloneNode(true);
      clone.hidden = false;
      this.replaceWith(clone);
    } else {
      this.hidden = true;
    }
  }

  /**
   * Toggles the expanded state of the volume pricing table
   */
  toggleExpanded() {
    this.classList.toggle('volume-pricing--expanded');
  }
}

if (!customElements.get('volume-pricing')) {
  customElements.define('volume-pricing', VolumePricingComponent);
}
